/* Lyla Beauty theme — storefront scripts (no dependencies). */
(() => {
  'use strict';

  const theme = window.theme || {};
  const strings = theme.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  /* ---------------------------------------------------------------
     Utilities
     --------------------------------------------------------------- */
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const formatMoney = (cents, format = theme.moneyFormat || '{{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${withSep}${decimal}${frac}` : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2);
      }
    });
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const toast = (message) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  /* ---------------------------------------------------------------
     Drawers (menu, cart) and overlay panels
     --------------------------------------------------------------- */
  const Drawer = {
    active: null,
    open(drawer, opener) {
      if (!drawer) return;
      if (this.active && this.active !== drawer) this.close(this.active, false);
      drawer.hidden = false;
      drawer._opener = opener || document.activeElement;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.body.classList.add('is-locked');
      const panel = drawer.querySelector('.drawer__panel');
      drawer._release = trapFocus(drawer);
      setTimeout(() => (panel || drawer).focus(), 50);
      this.active = drawer;
      const toggle = document.querySelector(`[aria-controls="${drawer.id}"]`);
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    },
    close(drawer = this.active, restoreFocus = true) {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      if (drawer._release) drawer._release();
      setTimeout(() => { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, reduceMotion ? 0 : 350);
      const toggle = document.querySelector(`[aria-controls="${drawer.id}"]`);
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus && drawer._opener) drawer._opener.focus();
      if (this.active === drawer) this.active = null;
    }
  };

  document.addEventListener('click', (e) => {
    const menuOpen = e.target.closest('[data-menu-open]');
    if (menuOpen) { Drawer.open(document.getElementById('MenuDrawer'), menuOpen); return; }

    const cartOpen = e.target.closest('[data-cart-open]');
    if (cartOpen && theme.cartType === 'drawer' && document.getElementById('CartDrawer')) {
      e.preventDefault();
      Drawer.open(document.getElementById('CartDrawer'), cartOpen);
      return;
    }

    if (e.target.closest('[data-drawer-close]')) { Drawer.close(e.target.closest('.drawer')); return; }

    const searchOpen = e.target.closest('[data-search-open]');
    if (searchOpen) { SearchModal.open(searchOpen); return; }
    if (e.target.closest('[data-search-close]')) { SearchModal.close(); return; }

    const toggle = e.target.closest('[data-customer-toggle]');
    if (toggle) {
      e.preventDefault();
      document.querySelectorAll('.customer__panel').forEach((p) => { p.hidden = p.id !== toggle.dataset.customerToggle; });
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (Drawer.active) Drawer.close();
    SearchModal.close();
    document.querySelectorAll('.facets.is-open').forEach((f) => f.closest('facet-filters')?.closeDrawer());
    document.querySelectorAll('details[data-hover-details][open]').forEach((d) => d.removeAttribute('open'));
  });

  const SearchModal = {
    el: null,
    open(opener) {
      this.el = document.querySelector('[data-search-modal]');
      if (!this.el) return;
      this.opener = opener;
      this.el.hidden = false;
      document.body.classList.add('is-locked');
      this.release = trapFocus(this.el);
      setTimeout(() => this.el.querySelector('input[type="search"]')?.focus(), 30);
    },
    close() {
      if (!this.el || this.el.hidden) return;
      this.el.hidden = true;
      document.body.classList.remove('is-locked');
      if (this.release) this.release();
      if (this.opener) this.opener.focus();
    }
  };

  /* ---------------------------------------------------------------
     Header: sticky behaviour and hover dropdowns
     --------------------------------------------------------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.mode = this.dataset.sticky;
      if (this.mode === 'none') return;
      this.section.classList.add('is-sticky');
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.setOffset();
      window.addEventListener('resize', debounce(() => this.setOffset(), 150));
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    setOffset() {
      document.documentElement.style.setProperty('--header-sticky-offset', `${this.offsetHeight}px`);
    }
    onScroll() {
      const y = window.scrollY;
      if (this.mode === 'scroll-up') {
        const hide = y > this.lastY && y > this.offsetHeight * 2;
        this.section.classList.toggle('is-hidden', hide);
        document.documentElement.style.setProperty('--header-sticky-offset', hide ? '0px' : `${this.offsetHeight}px`);
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  const initHoverMenus = (root = document) => {
    root.querySelectorAll('details[data-hover-details]').forEach((details) => {
      if (details._hoverInit) return;
      details._hoverInit = true;
      const item = details.closest('.nav__item');
      let timer;
      item.addEventListener('mouseenter', () => {
        if (!isDesktop()) return;
        clearTimeout(timer);
        document.querySelectorAll('details[data-hover-details][open]').forEach((d) => { if (d !== details) d.removeAttribute('open'); });
        details.setAttribute('open', '');
      });
      item.addEventListener('mouseleave', () => {
        if (!isDesktop()) return;
        timer = setTimeout(() => details.removeAttribute('open'), 120);
      });
      details.addEventListener('focusout', (e) => {
        if (!details.contains(e.relatedTarget)) details.removeAttribute('open');
      });
    });
  };
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-hover-details][open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });

  /* ---------------------------------------------------------------
     Predictive search
     --------------------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (this.dataset.enabled !== 'true' || !this.input) return;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
    }
    async search() {
      const q = this.input.value.trim();
      if (this.controller) this.controller.abort();
      if (!q) { this.results.innerHTML = ''; this.input.setAttribute('aria-expanded', 'false'); return; }
      this.controller = new AbortController();
      const params = new URLSearchParams({ q, section_id: 'predictive-search', 'resources[limit]': '6', 'resources[type]': 'product,collection,page,query', 'resources[options][unavailable_products]': 'last' });
      try {
        const res = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const section = doc.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : '';
        this.input.setAttribute('aria-expanded', 'true');
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------------------------------------------------------------
     Cart: add, change, drawer rendering
     --------------------------------------------------------------- */
  const Cart = {
    sectionIds() {
      return document.getElementById('CartDrawer') ? ['cart-drawer'] : [];
    },
    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = Math.min(count, 99);
        el.classList.toggle('is-empty', count === 0);
      });
    },
    renderDrawer(sections) {
      const html = sections && sections['cart-drawer'];
      const drawer = document.getElementById('CartDrawer');
      if (!html || !drawer) return;
      const fresh = parseHTML(html).querySelector('cart-drawer');
      if (!fresh) return;
      drawer.innerHTML = fresh.innerHTML;
      drawer.dataset.cartCountValue = fresh.dataset.cartCountValue;
      this.updateCount(Number(fresh.dataset.cartCountValue || 0));
    },
    async add(form, button) {
      const errorEl = form.querySelector('[data-form-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      const body = new FormData(form);
      const sections = this.sectionIds();
      if (sections.length) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
      }
      button?.classList.add('is-loading');
      button?.setAttribute('aria-disabled', 'true');
      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
        if (theme.cartType === 'drawer' && data.sections) {
          this.renderDrawer(data.sections);
          Drawer.open(document.getElementById('CartDrawer'), button);
        } else {
          const cart = await (await fetch(`${theme.routes.cart}.js`)).json();
          this.updateCount(cart.item_count);
          toast(strings.added);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
        else toast(err.message);
      } finally {
        button?.classList.remove('is-loading');
        button?.removeAttribute('aria-disabled');
      }
    },
    async change(line, quantity) {
      const drawer = document.getElementById('CartDrawer');
      drawer?.classList.add('is-updating');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
        this.renderDrawer(data.sections);
        this.updateCount(data.item_count);
      } catch (err) {
        toast(err.message);
      } finally {
        drawer?.classList.remove('is-updating');
      }
    }
  };

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const button = e.submitter || form.querySelector('[type="submit"]');
    if (button?.getAttribute('aria-disabled') === 'true') return;
    Cart.add(form, button);
  });

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty]');
        if (input) Cart.change(Number(input.dataset.cartQty), Math.max(0, parseInt(input.value, 10) || 0));
        const note = e.target.closest('[data-cart-note]');
        if (note) {
          fetch(`${theme.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
        }
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (!remove) return;
        e.preventDefault();
        Cart.change(Number(remove.dataset.cartRemove), 0);
      });
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
        if (!btn || !this.input) return;
        const step = Number(this.input.step) || 1;
        const min = this.input.min !== '' ? Number(this.input.min) : 0;
        const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
        const current = Number(this.input.value) || 0;
        const next = btn.hasAttribute('data-qty-plus') ? current + step : current - step;
        const clamped = Math.min(max, Math.max(min, next));
        if (clamped === current) return;
        this.input.value = clamped;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  // Cart page: submit the native form after quantity changes.
  document.addEventListener('change', debounce((e) => {
    const input = e.target.closest?.('[data-auto-submit-cart]');
    if (input) input.form.submit();
  }, 500));

  /* ---------------------------------------------------------------
     Sliders (scroll-snap based)
     --------------------------------------------------------------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.dots = [...this.querySelectorAll('[data-slide-to]')];
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.slideTo))));
      this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      this.update();
      const interval = Number(this.dataset.autoplay);
      if (interval && !reduceMotion && !window.Shopify?.designMode) this.autoplay(interval * 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    get slides() { return [...this.track.children].filter((el) => el.offsetParent !== null); }
    get index() {
      const slides = this.slides;
      if (!slides.length) return 0;
      const left = this.track.scrollLeft;
      let closest = 0;
      let min = Infinity;
      const padding = parseFloat(getComputedStyle(this.track).paddingLeft || 0);
      slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft - padding - left);
        if (d < min) { min = d; closest = i; }
      });
      return closest;
    }
    goTo(i) {
      const slides = this.slides;
      if (!slides[i]) return;
      const padding = parseFloat(getComputedStyle(this.track).paddingLeft || 0);
      this.track.scrollTo({ left: slides[i].offsetLeft - padding, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    go(dir) {
      const page = Math.max(1, Math.round(this.track.clientWidth / (this.slides[0]?.clientWidth || this.track.clientWidth)));
      const target = this.index + dir * page;
      const max = this.slides.length - 1;
      this.goTo(Math.max(0, Math.min(max, target)));
    }
    update() {
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      const i = this.index;
      this.dots.forEach((dot, n) => {
        dot.classList.toggle('is-active', n === i);
        if (n === i) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
      });
      this.dispatchEvent(new CustomEvent('slider:change', { detail: { index: i }, bubbles: true }));
    }
    autoplay(ms) {
      const tick = () => {
        const max = this.slides.length - 1;
        this.goTo(this.index >= max ? 0 : this.index + 1);
      };
      const start = () => { clearInterval(this.timer); this.timer = setInterval(tick, ms); };
      const stop = () => clearInterval(this.timer);
      this.addEventListener('mouseenter', stop);
      this.addEventListener('mouseleave', start);
      this.addEventListener('focusin', stop);
      this.addEventListener('focusout', start);
      start();
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------------------------------------------------------------
     Tabs (collection tabs, lifestyle tabs)
     --------------------------------------------------------------- */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.viewAll = this.querySelector('[data-tabs-view-all]');
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
          if (keys[e.key] === undefined) return;
          e.preventDefault();
          const next = (i + keys[e.key] + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active);
        tab.tabIndex = active ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
      });
      if (this.viewAll && this.tabs[index].dataset.viewAll) this.viewAll.href = this.tabs[index].dataset.viewAll;
    }
  }
  customElements.define('tabs-component', TabsComponent);

  const RecentlyViewed = {
    key: 'lyla:recently-viewed',
    read() {
      try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (_) { return []; }
    },
    store(handle) {
      try {
        const list = this.read().filter((h) => h !== handle);
        list.unshift(handle);
        localStorage.setItem(this.key, JSON.stringify(list.slice(0, 12)));
      } catch (_) { /* storage unavailable */ }
    }
  };

  /* ---------------------------------------------------------------
     Product: variant selection, gallery, sticky add to cart
     --------------------------------------------------------------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variant-json]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-variant-picker]')) this.onVariantChange();
      });
      this.markAvailability();
      if (this.dataset.productHandle) RecentlyViewed.store(this.dataset.productHandle);
      this.initStickyBar();
    }
    get selectedOptions() {
      const picker = this.querySelector('[data-variant-picker]');
      if (!picker) return [];
      const values = [];
      picker.querySelectorAll('select[data-option-index]').forEach((s) => { values[Number(s.dataset.optionIndex)] = s.value; });
      picker.querySelectorAll('input[data-option-index]:checked').forEach((r) => { values[Number(r.dataset.optionIndex)] = r.value; });
      return values;
    }
    onVariantChange() {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      options.forEach((value, i) => {
        const label = this.querySelector(`[data-selected-label="${i}"]`);
        if (label) label.textContent = value;
      });
      this.markAvailability();
      this.updateButtons(variant);
      if (!variant) return;
      this.querySelectorAll('[data-variant-id-input]').forEach((input) => { input.value = variant.id; });
      this.updatePrice(variant);
      this.updateInventory(variant);
      this.querySelectorAll('[data-sku]').forEach((el) => { el.textContent = variant.sku || ''; });
      if (variant.featured_media_id) {
        const gallery = this.querySelector('media-gallery');
        gallery?.showMedia(variant.featured_media_id);
      }
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    markAvailability() {
      const selected = this.selectedOptions;
      this.querySelectorAll('input[data-option-index]').forEach((input) => {
        const idx = Number(input.dataset.optionIndex);
        const available = this.variants.some((v) => v.available && v.options[idx] === input.value &&
          v.options.every((o, i) => i === idx || i > idx || o === selected[i]));
        input.nextElementSibling?.classList.toggle('is-unavailable', !available);
      });
    }
    updateButtons(variant) {
      const available = variant && variant.available;
      this.querySelectorAll('[data-add-button]').forEach((btn) => {
        btn.disabled = !available;
        const label = btn.querySelector('[data-add-label]');
        if (label) label.textContent = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;
      });
    }
    updatePrice(variant) {
      const onSale = variant.compare_at_price > variant.price;
      this.querySelectorAll('[data-price-container]').forEach((container) => {
        const old = container.querySelector('.price');
        const extra = old ? [...old.classList].filter((c) => !['price', 'price--sale', 'price--sold-out'].includes(c)).join(' ') : '';
        let badge = '';
        if (onSale && container.closest('.product__price, .spotlight__info')) {
          const label = theme.badgeStyle === 'percent'
            ? `-${Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)}%`
            : strings.onSale;
          badge = `<span class="badge badge--sale">${label}</span>`;
        }
        container.innerHTML = `<div class="price ${extra}${onSale ? ' price--sale' : ''}${variant.available ? '' : ' price--sold-out'}">` +
          `<span class="price__current">${formatMoney(variant.price)}</span>` +
          (onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>${badge}` : '') +
          '</div>';
      });
    }
    updateInventory(variant) {
      const el = this.querySelector('[data-inventory]');
      if (!el) return;
      const threshold = Number(el.dataset.threshold || 0);
      let html = '';
      if (!variant.available) html = `<span class="dot dot--out"></span>${strings.soldOut}`;
      else if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) {
        html = `<span class="dot dot--low"></span>${strings.lowStock.replace('[count]', variant.inventory_quantity)}`;
      } else html = `<span class="dot dot--ok"></span>${strings.inStock}`;
      el.innerHTML = html;
    }
    initStickyBar() {
      const bar = this.querySelector('[data-sticky-atc]');
      const target = this.querySelector('[data-main-buy]');
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        bar.querySelectorAll('button').forEach((b) => { b.tabIndex = show ? 0 : -1; });
      }).observe(target);
    }
  }
  customElements.define('product-info', ProductInfo);

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('slider-component');
      this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.slider?.goTo(Number(thumb.dataset.thumbIndex))));
      this.addEventListener('slider:change', (e) => this.setActiveThumb(e.detail.index));
      this.lightbox = this.querySelector('[data-lightbox]');
      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-zoom-open]');
        if (zoom && this.lightbox && !zoom.disabled) this.openLightbox(Number(zoom.dataset.zoomOpen));
        if (e.target.closest('[data-lightbox-close]')) this.lightbox?.close();
      });
    }
    setActiveThumb(index) {
      this.thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        if (i === index) { t.setAttribute('aria-current', 'true'); t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }
        else t.removeAttribute('aria-current');
      });
    }
    showMedia(mediaId) {
      const items = [...this.querySelectorAll('.gallery__item')];
      const index = items.findIndex((el) => el.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      if (this.closest('.product--grid') && isDesktop()) {
        // Grid layout: move the variant image to the first position.
        const track = items[0].parentElement;
        track.prepend(items[index]);
      } else {
        this.slider?.goTo(index);
      }
      this.setActiveThumb(index);
    }
    openLightbox(index) {
      if (typeof this.lightbox.showModal !== 'function') return;
      this.lightbox.showModal();
      const img = this.lightbox.querySelector(`[data-lightbox-index="${index}"]`);
      if (img) img.scrollIntoView({ block: 'start' });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-share]')?.addEventListener('click', async () => {
        const { url, title } = this.dataset;
        if (navigator.share) {
          try { await navigator.share({ url, title }); } catch (_) { /* dismissed */ }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          const msg = this.querySelector('[data-share-msg]');
          if (msg) msg.textContent = strings.shareCopied;
        } catch (_) { window.prompt('', url); }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ---------------------------------------------------------------
     Product recommendations & recently viewed
     --------------------------------------------------------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.product-grid')) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.querySelector('.product-grid')) this.innerHTML = fresh.innerHTML;
          else this.closest('.shopify-section')?.classList.add('is-empty');
        } catch (_) { /* leave section empty */ }
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  class RecentlyViewedSection extends HTMLElement {
    async connectedCallback() {
      const limit = Number(this.dataset.limit) || 4;
      const handles = RecentlyViewed.read().filter((h) => h !== this.dataset.exclude).slice(0, limit);
      if (!handles.length) return;
      const list = this.querySelector('[data-recent-list]');
      const template = this.querySelector('[data-recent-template]');
      const products = await Promise.all(handles.map((h) =>
        fetch(`${theme.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(h)}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      ));
      products.filter(Boolean).forEach((p) => {
        const node = template.content.cloneNode(true);
        node.querySelectorAll('[data-r-link]').forEach((a) => { a.href = p.url; });
        node.querySelector('[data-r-title]').textContent = p.title;
        const img = node.querySelector('[data-r-img]');
        if (p.featured_image) {
          const src = p.featured_image.startsWith('//') ? `https:${p.featured_image}` : p.featured_image;
          const url = new URL(src);
          url.searchParams.set('width', '600');
          img.src = url.toString();
          img.alt = p.title;
        } else img.remove();
        const price = node.querySelector('[data-r-price]');
        const onSale = p.compare_at_price > p.price;
        price.classList.toggle('price--sale', onSale);
        price.innerHTML = `<span class="price__current">${formatMoney(p.price)}</span>` + (onSale ? `<s class="price__compare">${formatMoney(p.compare_at_price)}</s>` : '');
        list.appendChild(node);
      });
      if (list.children.length) this.hidden = false;
    }
  }
  customElements.define('recently-viewed', RecentlyViewedSection);

  /* ---------------------------------------------------------------
     Collection filtering, sorting and load more (Section Rendering API)
     --------------------------------------------------------------- */
  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.max = Number(this.dataset.max) || 0;
      this.sMin = this.querySelector('[data-slider-min]');
      this.sMax = this.querySelector('[data-slider-max]');
      this.iMin = this.querySelector('[data-input-min]');
      this.iMax = this.querySelector('[data-input-max]');
      this.fill = this.querySelector('[data-fill]');
      const fromSlider = () => {
        let a = Number(this.sMin.value);
        let b = Number(this.sMax.value);
        if (a > b) [a, b] = [b, a];
        this.iMin.value = a > 0 ? a : '';
        this.iMax.value = b < this.max ? b : '';
        this.paint();
      };
      [this.sMin, this.sMax].forEach((s) => {
        s.addEventListener('input', fromSlider);
        s.addEventListener('change', () => this.iMax.dispatchEvent(new Event('change', { bubbles: true })));
      });
      [this.iMin, this.iMax].forEach((i) => i.addEventListener('input', () => {
        this.sMin.value = this.iMin.value || 0;
        this.sMax.value = this.iMax.value || this.max;
        this.paint();
      }));
      this.paint();
    }
    paint() {
      if (!this.fill || !this.max) return;
      const a = Math.min(Number(this.sMin.value), Number(this.sMax.value));
      const b = Math.max(Number(this.sMin.value), Number(this.sMax.value));
      this.fill.style.left = `${(a / this.max) * 100}%`;
      this.fill.style.right = `${100 - (b / this.max) * 100}%`;
    }
  }
  customElements.define('price-range', PriceRange);

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.onChange = debounce((e) => this.submit(e), 450);
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-sort-select]')) {
          const mirror = this.querySelector('[data-sort-mirror]');
          if (mirror) mirror.value = e.target.value;
          this.submit();
          return;
        }
        if (e.target.closest('[data-facet-form]')) this.onChange(e);
      });
      this.addEventListener('submit', (e) => {
        if (!e.target.matches('[data-facet-form]')) return;
        e.preventDefault();
        this.submit();
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.load(link.href); return; }
        if (e.target.closest('[data-facet-open]')) { this.openDrawer(); return; }
        if (e.target.closest('[data-facet-close]')) { this.closeDrawer(); return; }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); }
      });
      this._popstate = () => this.load(window.location.href, false);
      window.addEventListener('popstate', this._popstate);
    }
    disconnectedCallback() { window.removeEventListener('popstate', this._popstate); }
    openDrawer() {
      const drawer = this.querySelector('[data-facet-drawer]');
      if (!drawer) return;
      drawer.classList.add('is-open');
      document.body.classList.add('is-locked');
      this._release = trapFocus(drawer);
      drawer.querySelector('.facets__panel')?.focus();
    }
    closeDrawer() {
      const drawer = this.querySelector('[data-facet-drawer]');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      if (this._release) this._release();
    }
    buildUrl() {
      const form = this.querySelector('[data-facet-form]');
      const params = new URLSearchParams();
      if (form) {
        for (const [key, value] of new FormData(form)) if (value !== '') params.append(key, value);
      } else {
        const q = new URLSearchParams(window.location.search).get('q');
        if (q) params.set('q', q);
      }
      const sort = this.querySelector('[data-sort-select]');
      if (sort) params.set('sort_by', sort.value);
      const query = params.toString();
      return `${window.location.pathname}${query ? `?${query}` : ''}`;
    }
    submit() { this.load(this.buildUrl()); }
    async load(href, push = true) {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('page');
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      this.classList.add('is-loading');
      try {
        const res = await fetch(fetchUrl.toString());
        const doc = parseHTML(await res.text());
        const fresh = doc.querySelector('facet-filters');
        if (!fresh) throw new Error('No content');
        const swap = (selector) => {
          const current = this.querySelector(selector);
          const next = fresh.querySelector(selector);
          if (current && next) current.innerHTML = next.innerHTML;
        };
        swap('[data-results]');
        swap('[data-product-count]');
        swap('[data-facet-form]');
        swap('.facets__footer');
        if (push) window.history.pushState({}, '', url.pathname + url.search);
      } catch (_) {
        window.location.href = url.toString();
      } finally {
        this.classList.remove('is-loading');
      }
    }
    async loadMore(button) {
      button.classList.add('is-loading');
      const url = new URL(button.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      try {
        const doc = parseHTML(await (await fetch(url.toString())).text());
        const items = doc.querySelectorAll('[data-product-grid] > *');
        const grid = this.querySelector('[data-product-grid]');
        items.forEach((item) => grid.appendChild(item));
        const nextPagination = doc.querySelector('.pagination');
        const pagination = this.querySelector('.pagination');
        if (nextPagination) pagination.replaceWith(nextPagination); else pagination?.remove();
      } catch (_) {
        window.location.href = button.href;
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------------------------------------------------------------
     Deferred video (YouTube / Vimeo load only on click)
     --------------------------------------------------------------- */
  class DeferredVideo extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', () => {
        const { type, id } = this.dataset;
        const src = type === 'youtube'
          ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`
          : `https://player.vimeo.com/video/${id}?autoplay=1`;
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = this.querySelector('button')?.getAttribute('aria-label') || 'Video';
        this.replaceChildren(iframe);
      }, { once: true });
    }
  }
  customElements.define('deferred-video', DeferredVideo);

  /* ---------------------------------------------------------------
     Page-level behaviours
     --------------------------------------------------------------- */
  const initReveal = (root = document) => {
    const targets = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!document.body.classList.contains('has-reveal') || reduceMotion || window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    targets.forEach((el) => io.observe(el));
  };

  const initFooterAccordions = () => {
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    document.querySelectorAll('[data-footer-accordion]:not([data-static])').forEach((d) => {
      if (desktop) d.setAttribute('open', ''); else if (!d._touched) d.removeAttribute('open');
      if (!d._bound) {
        d._bound = true;
        d.querySelector('summary').addEventListener('click', (e) => {
          if (window.matchMedia('(min-width: 750px)').matches) e.preventDefault();
          d._touched = true;
        });
      }
    });
  };

  const initBackToTop = () => {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    btn.hidden = false;
    const toggle = () => btn.classList.toggle('is-visible', window.scrollY > 700);
    window.addEventListener('scroll', debounce(toggle, 50), { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));
    toggle();
  };

  const initMisc = () => {
    document.querySelectorAll('[data-auto-submit]').forEach((el) => el.addEventListener('change', () => el.form.submit()));
    document.querySelectorAll('form[data-confirm]').forEach((form) => form.addEventListener('submit', (e) => {
      if (!window.confirm(form.dataset.confirm)) e.preventDefault();
    }));
    document.querySelectorAll('select[data-default]').forEach((select) => { if (select.dataset.default) select.value = select.dataset.default; });
    if (window.location.hash === '#recover' && document.getElementById('RecoverPassword')) {
      document.getElementById('RecoverPassword').hidden = false;
      document.getElementById('CustomerLogin').hidden = true;
    }
  };

  const init = () => {
    initHoverMenus();
    initReveal();
    initFooterAccordions();
    initBackToTop();
    initMisc();
    window.addEventListener('resize', debounce(initFooterAccordions, 200));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* ---------------------------------------------------------------
     Theme editor support
     --------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', (e) => {
    initHoverMenus(e.target);
    initReveal(e.target);
    initFooterAccordions();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const tab = block.matches('[role="tab"]') ? block : null;
    if (tab) tab.click();
    const slider = block.closest('slider-component');
    if (slider && block.parentElement === slider.track) {
      slider.goTo([...slider.track.children].indexOf(block));
    }
  });
  document.addEventListener('shopify:section:unload', () => {
    document.body.classList.remove('is-locked');
  });

  window.LylaTheme = { Cart, Drawer, formatMoney };
})();
